<template>
  <controller>
    <main class="sgu-page">
      <header class="sgu-header">
        <div>
          <h1>SGU - Camadas de Performance</h1>
          <p>Rede Decisao - Unidades com visita semestral</p>
        </div>

        <div class="sgu-actions">
          <div class="sgu-action-row">
            <label v-if="!hasDirectorPermission" class="sgu-semester">
              <span>Ciclo</span>
              <select v-model="cycleId" :disabled="loading" @change="loadDashboard">
                <option disabled value="">Selecione</option>
                <option v-for="cycle in cycles" :key="cycle.id" :value="cycle.id">
                  {{ cycle.code }}
                </option>
              </select>
            </label>
            <router-link class="sgu-wizard-link" to="/appscripts/sgu/parametrizacao">
              Requisitos
            </router-link>
            <router-link class="sgu-wizard-link" :to="evaluationRoute">
              Avaliação
            </router-link>
            <router-link v-if="!hasDirectorPermission" class="sgu-wizard-link active" to="/appscripts/sgu">
              Dashboard
            </router-link>
            <router-link v-if="canViewResponses" class="sgu-wizard-link" to="/appscripts/sgu/respostas">
              Relatórios
            </router-link>
          </div>
          <div class="sgu-status">
            <span>{{ dataStatus }}</span>
          </div>
        </div>
      </header>

    <section class="sgu-shell">
      <nav class="sgu-tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          :class="['sgu-tab', { active: activeTab === tab.key }]"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div v-if="activeTab === 'geral'" class="sgu-filters" aria-label="Filtro regional">
        <button
          v-for="regional in regionalOptions"
          :key="regional"
          type="button"
          :class="{ active: selectedRegional === regional }"
          @click="selectedRegional = regional"
        >
          {{ regional }}
        </button>
      </div>

      <div v-else-if="activeTab === 'area'" class="sgu-filters" aria-label="Filtro de area">
        <button
          v-for="area in ['Todas', ...requirementAreas]"
          :key="area"
          type="button"
          :class="{ active: selectedArea === area }"
          @click="selectedArea = area"
        >
          {{ area }}
        </button>
      </div>

      <div v-else-if="activeTab === 'indicador'" class="sgu-filters" aria-label="Filtro de indicador">
        <button
          v-for="indicator in indicatorOptions"
          :key="indicator"
          type="button"
          :class="{ active: activeIndicatorName === indicator }"
          @click="selectedIndicator = indicator"
        >
          {{ indicator }}
        </button>
      </div>

      <div v-else class="sgu-requirement-filters">
        <div>
          <span>Area</span>
          <div class="sgu-filters" aria-label="Filtro de requisito por area">
            <button
              v-for="area in ['Todas', ...requirementAreas]"
              :key="area"
              type="button"
              :class="{ active: selectedRequirementArea === area }"
              @click="selectRequirementArea(area)"
            >
              {{ area }}
            </button>
          </div>
        </div>

        <div>
          <span>Requisito</span>
          <div class="sgu-filters" aria-label="Filtro por requisito">
            <button
              v-for="requirement in filteredRequirements"
              :key="requirement.id"
              type="button"
              :class="{ active: activeRequirement?.id === requirement.id }"
              @click="selectedRequirementId = requirement.id"
            >
              ID-{{ requirement.referenceId || requirement.id }} - {{ requirement.name }}
            </button>
          </div>
        </div>
      </div>

    </section>

    <section v-if="activeTab === 'geral'" class="sgu-overview">
      <div class="sgu-grid">
        <article v-for="item in kpis" :key="item.label" class="sgu-kpi" :style="{ borderTopColor: item.color }">
          <span>{{ item.label }}</span>
          <strong :style="{ color: item.color }">{{ item.value }}</strong>
          <small>{{ item.helper }}</small>
        </article>
      </div>

      <div v-if="overviewUnits.length" class="sgu-overview-grid">
        <article class="sgu-card sgu-overview-card">
          <h2>Nota por unidade</h2>
          <p>Nota geral ponderada, ordenada do maior para o menor resultado.</p>
          <div class="sgu-bars">
            <div v-for="unit in overviewUnits" :key="unit.name" class="sgu-bar-row">
              <strong>{{ unit.name }}</strong>
              <div class="sgu-bar-track"><span :style="{ width: `${unit.score}%`, backgroundColor: unit.color }" /></div>
              <span>{{ unit.score }}%</span>
            </div>
          </div>
        </article>

        <article class="sgu-card sgu-overview-card">
          <h2>Como o DU se classifica em comparacao ao CSC</h2>
          <p>Saldo positivo indica que o DU se avaliou acima em mais requisitos; saldo negativo indica abaixo.</p>
          <div class="sgu-chart sgu-comparison-chart" :style="{ height: `${comparisonChartHeight}px` }">
            <Bar :data="duComparisonChartData" :options="duComparisonChartOptions" :plugins="[zeroLinePlugin]" />
          </div>
        </article>

        <article class="sgu-card sgu-overview-card">
          <h2>Desempenho por tema</h2>
          <p>Media das notas por tema, agrupada pela camada de classificacao da unidade.</p>
          <div class="sgu-chart-legend">
            <span v-for="group in themeGroups" :key="group.label">
              <i :style="{ backgroundColor: group.color }" />{{ group.name }} ({{ group.units.length }})
            </span>
          </div>
          <div v-if="themePerformance.themes.length" class="sgu-chart sgu-theme-chart">
            <Bar :data="themeChartData" :options="themeChartOptions" />
          </div>
          <div v-else class="sgu-inline-empty">Nenhuma área da mandala disponível neste ciclo.</div>
        </article>

        <article class="sgu-card sgu-overview-card">
          <h2>Requisitos com baixa variabilidade</h2>
          <p>Requisitos em que pelo menos 85% das unidades ficaram no mesmo nivel.</p>
          <div v-if="lowVariabilityRequirements.length" class="sgu-requirement-list">
            <div v-for="requirement in lowVariabilityRequirements" :key="requirement.id">
              <div><strong>ID-{{ requirement.referenceId || requirement.id }} - {{ requirement.name }}</strong><span>{{ requirement.dominance }}%</span></div>
              <small>Predominio: {{ requirement.dominantLevel }}</small>
            </div>
          </div>
          <div v-else class="sgu-inline-empty">Nenhum requisito com baixa variabilidade.</div>
        </article>

        <article class="sgu-card sgu-overview-card consolidated">
          <h2>Requisitos mais consolidados</h2>
          <p>Mostra quantas unidades ficaram em cada camada. A media vai de 0 (Nao Atende) a 3 (Alta Performance).</p>
          <div class="sgu-ranked-header"><span /><span>Requisito</span><span>Media</span></div>
          <div class="sgu-ranked-list">
            <button v-for="(requirement, index) in consolidatedRequirements" :key="requirement.id" type="button" @click="goToRequirement(requirement)">
              <span>{{ index + 1 }}</span>
              <div>
                <strong>{{ requirement.name }}</strong>
                <small>ID-{{ requirement.referenceId || requirement.id }}<template v-if="requirement.area"> - {{ requirement.area }}</template></small>
                <div class="sgu-ranked-distribution">
                  <em v-for="layer in requirementDistribution(requirement)" :key="layer.key" :class="layer.className">{{ layer.value }} {{ layer.label }}</em>
                </div>
              </div>
              <b :style="{ color: requirementAverageColor(requirement.average) }">{{ formatRequirementAverage(requirement.average) }}</b>
            </button>
          </div>
        </article>

        <article class="sgu-card sgu-overview-card difficulty">
          <h2>Requisitos com mais dificuldade</h2>
          <p>Os 10 requisitos com menor media entre as unidades avaliadas. A media vai de 0 (Nao Atende) a 3 (Alta Performance).</p>
          <div class="sgu-ranked-header"><span /><span>Requisito</span><span>Media</span></div>
          <div class="sgu-ranked-list">
            <button v-for="(requirement, index) in difficultRequirements" :key="requirement.id" type="button" @click="goToRequirement(requirement)">
              <span>{{ index + 1 }}</span>
              <div>
                <strong>{{ requirement.name }}</strong>
                <small>ID-{{ requirement.referenceId || requirement.id }}<template v-if="requirement.area"> - {{ requirement.area }}</template></small>
                <div class="sgu-ranked-distribution">
                  <em v-for="layer in requirementDistribution(requirement)" :key="layer.key" :class="layer.className">{{ layer.value }} {{ layer.label }}</em>
                </div>
              </div>
              <b :style="{ color: requirementAverageColor(requirement.average) }">{{ formatRequirementAverage(requirement.average) }}</b>
            </button>
          </div>
        </article>
      </div>

      <div v-else class="sgu-empty sgu-overview-empty">
        Este ciclo ainda não possui avaliações suficientes para visualizar o painel geral.
      </div>
    </section>

    <section v-else-if="activeTab === 'area'" class="sgu-content">
      <div
        v-if="areaSummaries.length"
        :class="['sgu-area-detail-layout', { selected: selectedArea !== 'Todas' }]"
      >
        <div class="sgu-area-grid">
          <article v-for="area in areaSummaries" :key="area.name" class="sgu-card sgu-area-card">
            <div class="sgu-card-heading">
              <div>
                <h2>{{ area.name }}</h2>
                <p>{{ area.requirements }} requisitos avaliados</p>
              </div>
              <strong>{{ area.score }}%</strong>
            </div>
            <div class="sgu-distribution">
              <div><span>Alta performance</span><strong>{{ area.highPerformance }}</strong></div>
              <div><span>Sustentavel</span><strong>{{ area.sustainable }}</strong></div>
              <div><span>Basico</span><strong>{{ area.basic }}</strong></div>
            </div>
          </article>
        </div>

        <article v-if="selectedArea !== 'Todas'" class="sgu-card sgu-area-requirements">
          <div class="sgu-card-heading">
            <div>
              <h2>Requisitos da area</h2>
              <p>{{ selectedArea }} - {{ selectedAreaRequirements.length }} requisitos</p>
            </div>
          </div>

          <div v-if="selectedAreaRequirements.length" class="sgu-table-wrap">
            <table class="sgu-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Requisito</th>
                  <th>Media</th>
                  <th>AP</th>
                  <th>SU</th>
                  <th>BA</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="requirement in selectedAreaRequirements" :key="requirement.id">
                  <td><strong>ID-{{ requirement.referenceId || requirement.id }}</strong></td>
                  <td>{{ requirement.name }}</td>
                  <td><span class="sgu-score">{{ requirementScore(requirement.average) }}%</span></td>
                  <td>{{ distributionValue(requirement, 'highPerformance', 'ap') }}</td>
                  <td>{{ distributionValue(requirement, 'sustainable', 'su') }}</td>
                  <td>{{ distributionValue(requirement, 'basic', 'ba') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="sgu-inline-empty">Nenhum requisito encontrado para esta area.</div>
        </article>
      </div>
      <div v-else class="sgu-empty">
        Este ciclo ainda não possui avaliações suficientes de requisitos para visualizar as areas.
      </div>
    </section>

    <section v-else-if="activeTab === 'indicador'" class="sgu-content">
      <div v-if="activeIndicatorName" class="sgu-indicator-detail">
        <p class="sgu-indicator-intro">
          Classificacao baseada na <strong>media dos requisitos associados a este indicador</strong> -
          nao e a classificacao geral da unidade no SGU.
        </p>

        <div class="sgu-indicator-big-numbers">
          <article
            v-for="layer in indicatorLayers"
            :key="layer.label"
            class="sgu-kpi"
            :style="{ borderTopColor: layer.color }"
          >
            <span>{{ layer.label }}</span>
            <strong :style="{ color: layer.color }">{{ layer.value }}</strong>
            <small>no indicador</small>
          </article>
        </div>

        <div class="sgu-indicator-analysis">
          <article class="sgu-card sgu-indicator-chart-card">
            <h2>Desempenho nos requisitos x resultado {{ activeIndicatorName }}</h2>
            <p>
              Desempenho nos Requisitos de {{ activeIndicatorName }}: nota SGU calculada com os
              {{ indicatorRequirements.length }} requisitos associados - {{ indicatorPoints.length }} unidades com dados
            </p>
            <div v-if="indicatorPoints.length >= 3" class="sgu-indicator-chart">
              <Scatter
                :data="indicatorChartData"
                :options="indicatorChartOptions"
                :plugins="[indicatorLabelsPlugin]"
              />
            </div>
            <div v-else class="sgu-inline-empty">{{ indicatorResultsMessage }}</div>
            <div class="sgu-indicator-legend">
              <span><i style="background:#ba7517" />Basico no indicador</span>
              <span><i style="background:#3b6d11" />Sustentavel</span>
              <span><i style="background:#0c447c" />Alta Performance</span>
            </div>
            <p class="sgu-indicator-explanation">
              Este grafico ajuda a entender se as unidades que melhor atendem aos requisitos avaliados
              tambem alcancam melhores resultados no indicador. Se houver uma tendencia positiva, e um
              sinal de que os requisitos podem estar relacionados ao bom desempenho. Se essa relacao nao
              for observada, pode ser necessario <strong>revisar os requisitos</strong>,
              <strong>investigar outros fatores</strong> que influenciam o resultado ou
              <strong>apoiar as unidades</strong> na aplicacao dessas praticas.
            </p>
          </article>

          <article class="sgu-card sgu-associated-requirements">
            <h2>Requisitos associados a {{ activeIndicatorName }}</h2>
            <p>Clique para ver o detalhamento por requisito</p>
            <button
              v-for="requirement in indicatorRequirements"
              :key="requirement.id"
              type="button"
              @click="goToRequirement(requirement)"
            >
              <span>
                <small>ID-{{ requirement.referenceId || requirement.id }} - {{ requirement.area }}</small>
                <strong>{{ requirement.name }}</strong>
              </span>
              <b>&rarr;</b>
            </button>
          </article>
        </div>

        <article class="sgu-card sgu-indicator-results">
          <h2>Resultado por unidade</h2>
          <div class="sgu-table-wrap">
            <table class="sgu-table">
              <thead>
                <tr>
                  <th>Unidade</th>
                  <th>Nivel no indicador (IDI)</th>
                  <th>Nivel geral SGU</th>
                  <th>Desempenho nos Requisitos de {{ activeIndicatorName }}</th>
                  <th>Resultado {{ activeIndicatorName }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="unit in indicatorRows" :key="unit.name">
                  <td><strong>{{ unit.name }}</strong></td>
                  <td>
                    <span :class="['sgu-layer-badge', layerClass(unit.indicatorLayer)]">
                      {{ unit.indicatorLayer || '-' }}
                    </span>
                    <small v-if="unit.differsFromSgu" class="sgu-layer-difference">!= SGU</small>
                  </td>
                  <td>
                    <span :class="['sgu-layer-badge', layerClass(unit.sguLayer)]">{{ unit.sguLayer }}</span>
                  </td>
                  <td class="sgu-result-number">{{ unit.idi !== null ? `${unit.idi}%` : '-' }}</td>
                  <td class="sgu-result-number">{{ unit.result !== null ? `${unit.result}%` : 'N/A' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
      </div>
      <div v-else class="sgu-empty">
        Nao foi possivel carregar os requisitos e resultados dos indicadores da planilha.
      </div>
    </section>

    <section v-else class="sgu-content">
      <div v-if="activeRequirement" class="sgu-requirement-detail">
        <div class="sgu-requirement-title">
          <strong>{{ activeRequirement.name }}</strong>
          <span>{{ activeRequirement.area || 'Sem area' }}</span>
          <span v-if="activeRequirement.indicator">Indicador: {{ activeRequirement.indicator }}</span>
        </div>

        <div class="sgu-layer-descriptions">
          <div class="basic"><strong>Basico</strong><p>{{ requirementDescription('basic') }}</p></div>
          <div class="sustainable"><strong>Sustentavel</strong><p>{{ requirementDescription('sustainable') }}</p></div>
          <div class="high"><strong>Alta Performance</strong><p>{{ requirementDescription('highPerformance') }}</p></div>
        </div>

        <div class="sgu-requirement-kpis">
          <article v-for="layer in activeRequirementLayers" :key="layer.label" :class="layer.className">
            <span>{{ layer.label }}</span>
            <strong>{{ layer.value }}</strong>
          </article>
        </div>

        <div class="sgu-requirement-results">
          <article class="sgu-card sgu-requirement-chart-card">
            <h2>Relacao com o resultado do indicador</h2>
            <template v-if="requirementHasIndicator">
              <p>{{ requirementPoints.length }} unidades com dados de resultado - {{ activeRequirement.indicator }}</p>
              <div v-if="requirementPoints.length" class="sgu-requirement-chart">
                <Scatter :data="requirementChartData" :options="requirementChartOptions" />
              </div>
              <div v-else class="sgu-inline-empty">{{ indicatorResultsMessage }}</div>
              <div class="sgu-requirement-legend">
                <span><i style="background:#e24b4a" />Nao Atende</span>
                <span><i style="background:#ba7517" />Basico</span>
                <span><i style="background:#3b6d11" />Sustentavel</span>
                <span><i style="background:#0c447c" />Alta Performance</span>
              </div>
              <p class="sgu-requirement-explanation">
                Este grafico ajuda a entender se as unidades que melhor atendem aos requisitos avaliados
                tambem alcancam melhores resultados no indicador. Se houver uma tendencia positiva, e um
                sinal de que o requisito pode estar relacionado ao bom desempenho. Se essa relacao nao for
                observada, pode ser necessario <strong>revisar o requisito</strong>,
                <strong>investigar outros fatores</strong> ou <strong>apoiar as unidades</strong> na aplicacao da pratica.
              </p>
            </template>
            <div v-else class="sgu-no-indicator">
              <strong>Sem indicador associado.</strong>
              <p>Este requisito nao esta vinculado a um indicador de resultado, portanto nao e possivel analisar sua relacao com desempenho mensuravel.</p>
            </div>
          </article>
          <article class="sgu-card sgu-requirement-unit-results">
            <h2>Resultado por unidade</h2>
            <p>Ordenado por nivel no requisito</p>
            <div class="sgu-table-wrap">
              <table class="sgu-table">
                <thead>
                  <tr>
                    <th>Unidade</th>
                    <th>Nivel</th>
                    <th>{{ activeRequirement.indicator || 'Indicador' }}</th>
                    <th>Nota SGU</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="unit in requirementUnitRows" :key="unit.name">
                    <td><strong>{{ unit.name }}</strong></td>
                    <td><span :class="['sgu-layer-badge', layerClass(unit.level)]">{{ unit.level || '-' }}</span></td>
                    <td>{{ unit.result !== null ? `${unit.result}%` : 'N/A' }}</td>
                    <td class="sgu-muted-result">{{ unit.sguScore }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </div>
      <div v-else class="sgu-empty">
        Este ciclo ainda não possui avaliações suficientes de requisitos para visualizar os detalhes.
      </div>
    </section>

      <p v-if="errorMessage" class="sgu-error">{{ errorMessage }}</p>
    </main>
  </controller>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { averageThemeScore, buildThemePerformance } from './sguDashboard.helpers'
import { Bar, Scatter } from 'vue-chartjs'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js'
import ImportedModulesService from '../services/importedModules.service'
import UserService from '@/services/user.service'

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend)

const canViewResponses = UserService.filterPermissions('visualizarRespostasSgu')
const canFillCscAssessment = UserService.filterPermissions('preencherAvaliacaoSguCsc')
const hasDirectorPermission = UserService.filterPermissions('preencherAvaliacaoSgu')
const evaluationRoute = hasDirectorPermission
  ? '/appscripts/sgu/wizard'
  : canFillCscAssessment ? '/appscripts/sgu/avaliacao-csc' : '/appscripts/sgu/wizard'

const tabs = [
  { key: 'geral', label: 'Visao Geral' },
  { key: 'area', label: 'Por Area' },
  { key: 'indicador', label: 'Por Indicador' },
  { key: 'requisito', label: 'Por Requisito' }
]

const REGIONALS = {
  Lucimar: ['CAV', 'CSB', 'CTM', 'DAF', 'DEC', 'GRA', 'LOU', 'UNI'],
  Elvis: ['CMV', 'CNE', 'CSP', 'FAT', 'GRU', 'POP'],
  Lara: ['ACN', 'HOR', 'MAS', 'OSB', 'RVT', 'VIL', 'VIN'],
  Marlos: ['CRE', 'RUI', 'UMC']
}
const regionalOptions = ['Todas', ...Object.keys(REGIONALS)]

const activeTab = ref('geral')
const cycleId = ref('')
const cycles = ref([])
const loading = ref(false)
const errorMessage = ref('')
const dataStatus = ref('Carregando ciclo...')
const dashboardData = ref(null)
const selectedRequirementArea = ref('Todas')
const selectedRequirementId = ref('')
const selectedArea = ref('Todas')
const selectedRegional = ref('Todas')
const selectedIndicator = ref('')

onMounted(loadDashboard)

const kpis = computed(() => {
  const units = filteredDashboardUnits.value
  const average = units.length
    ? Math.round(units.reduce((total, unit) => total + parseNumber(unit.Nota), 0) / units.length)
    : null
  const highPerformance = countUnitsByLayer(units, 'Alta Performance')
  const sustainable = countUnitsByLayer(units, 'Sustentável')
  const basic = countUnitsByLayer(units, 'Básico')
  const percentage = value => units.length ? Math.round((value / units.length) * 100) : 0

  return [
    { label: 'Unidades', value: units.length || '-', helper: units.length ? 'com visita' : 'Aguardando ingestao', color: '#c0001d' },
    { label: 'Nota media', value: average !== null ? `${average}%` : '-', helper: average !== null ? `Camada: ${scoreLayer(average)}` : 'Aguardando ingestao', color: average !== null ? scoreColor(average) : '#aaa' },
    { label: 'Alta performance', value: highPerformance, helper: `${percentage(highPerformance)}% das unidades`, color: '#0d3b8c' },
    { label: 'Sustentavel', value: sustainable, helper: `${percentage(sustainable)}% das unidades`, color: '#1a6b3a' },
    { label: 'Basico', value: basic, helper: basic ? `${percentage(basic)}% das unidades` : 'Nenhuma', color: '#bf9000' }
  ]
})

const requirements = computed(() => dashboardData.value?.requirements || [])
const filteredDashboardUnits = computed(() => {
  const units = dashboardData.value?.units || []
  if (selectedRegional.value === 'Todas') return units

  const regionalUnits = REGIONALS[selectedRegional.value] || []
  return units.filter(unit => regionalUnits.includes(unit.Unidade))
})
const requirementAreas = computed(() => {
  return [...new Set(requirements.value.map(item => item.area || 'Sem area'))]
    .sort((a, b) => a.localeCompare(b))
})
const filteredRequirements = computed(() => {
  if (selectedRequirementArea.value === 'Todas') return requirements.value

  return requirements.value.filter(item => (item.area || 'Sem area') === selectedRequirementArea.value)
})
const activeRequirement = computed(() => {
  return filteredRequirements.value.find(item => item.id === selectedRequirementId.value) || filteredRequirements.value[0] || null
})
const activeRequirementLayers = computed(() => {
  if (!activeRequirement.value) return []

  return [
    { label: 'Basico', value: distributionValue(activeRequirement.value, 'basic', 'ba'), className: 'basic' },
    { label: 'Sustentavel', value: distributionValue(activeRequirement.value, 'sustainable', 'su'), className: 'sustainable' },
    { label: 'Alta Performance', value: distributionValue(activeRequirement.value, 'highPerformance', 'ap'), className: 'high' },
  ]
})
const selectedAreaRequirements = computed(() => {
  if (selectedArea.value === 'Todas') return []

  return requirements.value
    .filter(item => (item.area || 'Sem area') === selectedArea.value)
    .sort((a, b) => Number(b.average) - Number(a.average))
})
const areaSummaries = computed(() => {
  const grouped = requirements.value.reduce((areas, requirement) => {
    const areaName = requirement.area || 'Sem area'
    if (!areas[areaName]) {
      areas[areaName] = {
        name: areaName,
        requirements: 0,
        averageTotal: 0,
        highPerformance: 0,
        sustainable: 0,
        basic: 0,
        notMet: 0
      }
    }

    const area = areas[areaName]
    area.requirements += 1
    area.averageTotal += Number(requirement.average || 0)
    area.highPerformance += distributionValue(requirement, 'highPerformance', 'ap')
    area.sustainable += distributionValue(requirement, 'sustainable', 'su')
    area.basic += distributionValue(requirement, 'basic', 'ba')
    area.notMet += distributionValue(requirement, 'notMet', 'na')

    return areas
  }, {})

  const summaries = Object.values(grouped)
    .map(area => ({
      ...area,
      score: requirementScore(area.averageTotal / area.requirements)
    }))
    .sort((a, b) => b.score - a.score)

  return selectedArea.value === 'Todas'
    ? summaries
    : summaries.filter(area => area.name === selectedArea.value)
})
const indicatorOptions = computed(() => {
  return [...new Set(requirements.value
    .map(requirement => requirement.indicator)
    .filter(indicator => indicator && indicator.toLowerCase() !== 'n/a'))]
    .sort((a, b) => a.localeCompare(b))
})
const activeIndicatorName = computed(() => {
  return indicatorOptions.value.includes(selectedIndicator.value)
    ? selectedIndicator.value
    : indicatorOptions.value[0] || ''
})
const indicatorRequirements = computed(() => {
  return requirements.value.filter(requirement => requirement.indicator === activeIndicatorName.value)
})
const indicatorResultsMessage = computed(() => {
  const status = dashboardData.value?.indicatorResultsStatus
  if (status === 'unavailable') return 'Não foi possível carregar os resultados dos indicadores. Atualize a página para tentar novamente.'
  if (status === 'not_configured') return 'A fonte dos resultados dos indicadores ainda não foi configurada para este ciclo.'
  if (status === 'empty') return 'A fonte dos indicadores ainda não possui resultados para este ciclo.'
  return 'Dados insuficientes para visualizacao.'
})
const levelsByUnit = computed(() => {
  return (dashboardData.value?.levels || []).reduce((map, level) => {
    if (!map[level.unit]) map[level.unit] = {}
    map[level.unit][String(level.requirementId)] = level.levelNumber
    return map
  }, {})
})
const requirementLevelsByUnit = computed(() => {
  if (!activeRequirement.value) return {}

  return (dashboardData.value?.levels || []).reduce((map, level) => {
    if (String(level.requirementId) === String(activeRequirement.value.id)) {
      map[level.unit] = {
        level: level.level || '',
        levelNumber: level.levelNumber
      }
    }
    return map
  }, {})
})
const requirementHasIndicator = computed(() => {
  const indicator = activeRequirement.value?.indicator
  return Boolean(indicator && indicator.toLowerCase() !== 'n/a')
})
const requirementUnitRows = computed(() => {
  const indicator = activeRequirement.value?.indicator
  const results = dashboardData.value?.indicatorResults || {}

  return (dashboardData.value?.units || []).map(unit => {
    const level = requirementLevelsByUnit.value[unit.Unidade] || { level: '', levelNumber: null }
    const result = requirementHasIndicator.value ? results[unit.Unidade]?.[indicator] : null

    return {
      name: unit.Unidade,
      level: level.level,
      levelNumber: level.levelNumber,
      result: result === null || result === undefined ? null : Number(result),
      sguScore: parseNumber(unit.Nota)
    }
  }).sort((a, b) => (b.levelNumber ?? -1) - (a.levelNumber ?? -1))
})
const requirementPoints = computed(() => {
  return requirementUnitRows.value
    .filter(unit => unit.levelNumber !== null && unit.levelNumber !== undefined && unit.result !== null)
    .map(unit => ({
      x: unit.levelNumber + requirementJitter(unit.name),
      y: unit.result,
      unit: unit.name,
      level: unit.level
    }))
})
const requirementChartData = computed(() => ({
  datasets: [{
    label: 'Unidades',
    data: requirementPoints.value,
    backgroundColor: requirementPoints.value.map(point => `${layerColor(point.level)}cc`),
    borderColor: requirementPoints.value.map(point => layerColor(point.level)),
    pointRadius: 8,
    pointHoverRadius: 10
  }]
}))
const requirementChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context => {
          const point = requirementPoints.value[context.dataIndex]
          return point
            ? [` ${point.unit}`, ` Nivel: ${point.level}`, ` ${activeRequirement.value.indicator}: ${point.y}%`]
            : []
        }
      }
    }
  },
  scales: {
    x: {
      min: -0.5,
      max: 3.5,
      ticks: {
        stepSize: 1,
        callback: value => ['Nao Atende', 'Basico', 'Sustentavel', 'Alta Performance'][Math.round(value)] || '',
        font: { size: 9 }
      },
      grid: { color: 'rgba(0,0,0,.05)' }
    },
    y: {
      title: {
        display: true,
        text: `${activeRequirement.value?.indicator || 'Indicador'} (%)`,
        font: { size: 10 },
        color: '#888'
      },
      ticks: { callback: value => `${value}%`, font: { size: 10 } },
      grid: { color: 'rgba(0,0,0,.05)' }
    }
  }
}))
const indicatorRows = computed(() => {
  const results = dashboardData.value?.indicatorResults || {}

  return (dashboardData.value?.units || []).map(unit => {
    let points = 0
    let maximum = 0

    indicatorRequirements.value.forEach(requirement => {
      const level = levelsByUnit.value[unit.Unidade]?.[String(requirement.id)]
      if (level === null || level === undefined) return

      const weight = Number(requirement.weight || 1)
      points += Number(level) * weight
      maximum += 3 * weight
    })

    const idi = maximum > 0 ? Math.round((points / maximum) * 100) : null
    const indicatorLayer = idi === null ? null : scoreLayer(idi)
    const sguLayer = scoreLayer(parseNumber(unit.Nota))
    const result = results[unit.Unidade]?.[activeIndicatorName.value]

    return {
      name: unit.Unidade,
      idi,
      indicatorLayer,
      sguLayer,
      sguScore: parseNumber(unit.Nota),
      result: result === null || result === undefined ? null : Number(result),
      differsFromSgu: indicatorLayer !== null && indicatorLayer !== sguLayer
    }
  }).sort((a, b) => (b.idi ?? -1) - (a.idi ?? -1))
})
const indicatorDistribution = computed(() => {
  return indicatorRows.value.reduce((distribution, unit) => {
    if (unit.indicatorLayer === 'Alta Performance') distribution.highPerformance++
    else if (unit.indicatorLayer === 'Sustentável') distribution.sustainable++
    else if (unit.indicatorLayer === 'Básico') distribution.basic++
    else if (unit.indicatorLayer === 'Não Atende') distribution.notMet++
    return distribution
  }, { notMet: 0, basic: 0, sustainable: 0, highPerformance: 0 })
})
const indicatorLayers = computed(() => [
  { label: 'Nao Atende', value: indicatorDistribution.value.notMet, color: '#e24b4a' },
  { label: 'Basico', value: indicatorDistribution.value.basic, color: '#ba7517' },
  { label: 'Sustentavel', value: indicatorDistribution.value.sustainable, color: '#3b6d11' },
  { label: 'Alta Performance', value: indicatorDistribution.value.highPerformance, color: '#0c447c' }
])
const indicatorPoints = computed(() => {
  return indicatorRows.value
    .filter(unit => unit.idi !== null && unit.result !== null)
    .map(unit => ({
      x: unit.idi,
      y: unit.result,
      label: unit.name,
      layer: unit.indicatorLayer,
      sguScore: unit.sguScore
    }))
})
const indicatorChartData = computed(() => ({
  datasets: [{
    label: 'Unidades',
    data: indicatorPoints.value,
    backgroundColor: indicatorPoints.value.map(point => `${layerColor(point.layer)}cc`),
    borderColor: indicatorPoints.value.map(point => layerColor(point.layer)),
    pointRadius: 9,
    pointHoverRadius: 11
  }]
}))
const indicatorChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label: context => {
          const point = indicatorPoints.value[context.dataIndex]
          return point
            ? [` ${point.label}`, ` IDI: ${point.x}%`, ` ${activeIndicatorName.value}: ${point.y}%`, ` Nota SGU: ${point.sguScore}%`]
            : []
        }
      }
    }
  },
  scales: {
    x: {
      title: {
        display: true,
        text: `Desempenho nos Requisitos de ${activeIndicatorName.value} (0-100)`,
        font: { size: 10 },
        color: '#888'
      },
      min: 0,
      max: 100,
      ticks: { callback: value => `${value}%`, font: { size: 9 } },
      grid: { color: 'rgba(0,0,0,.05)' }
    },
    y: {
      title: {
        display: true,
        text: `Atingimento da meta de ${activeIndicatorName.value} (%)`,
        font: { size: 10 },
        color: '#888'
      },
      ticks: { callback: value => `${value}%`, font: { size: 10 } },
      grid: { color: 'rgba(0,0,0,.05)' }
    }
  }
}))
const indicatorLabelsPlugin = {
  id: 'sgu-indicator-labels',
  afterDatasetsDraw (chart) {
    const context = chart.ctx
    const points = chart.data.datasets[0]?.data || []
    const elements = chart.getDatasetMeta(0)?.data || []

    context.save()
    context.fillStyle = '#333'
    context.font = 'bold 9px sans-serif'
    context.textAlign = 'center'
    points.forEach((point, index) => {
      const element = elements[index]
      if (element) context.fillText(point.label, element.x, element.y - 12)
    })
    context.restore()
  }
}
const overviewUnits = computed(() => {
  return filteredDashboardUnits.value
    .map(unit => ({
      name: unit.Unidade,
      score: parseNumber(unit.Nota),
      layer: unit.Camada,
      color: scoreColor(parseNumber(unit.Nota))
    }))
    .sort((a, b) => b.score - a.score)
})
const duComparisonUnits = computed(() => {
  return filteredDashboardUnits.value.map(unit => {
    const above = parseNumber(unit['Div. Superior'])
    const below = parseNumber(unit['Div. Inferior'])

    return {
      name: unit.Unidade,
      above,
      below,
      balance: above - below
    }
  }).sort((a, b) => b.balance - a.balance)
})
const comparisonChartHeight = computed(() => Math.max(400, duComparisonUnits.value.length * 15 + 20))
const duComparisonChartData = computed(() => ({
  labels: duComparisonUnits.value.map(unit => unit.name),
  datasets: [{
    data: duComparisonUnits.value.map(unit => unit.balance),
    backgroundColor: duComparisonUnits.value.map(unit => unit.balance > 0 ? '#8B0000cc' : unit.balance < 0 ? '#FF6B6Bcc' : '#cccccccc'),
    borderColor: duComparisonUnits.value.map(unit => unit.balance > 0 ? '#8B0000' : unit.balance < 0 ? '#FF6B6B' : '#cccccc'),
    borderWidth: 1,
    borderRadius: 3,
    barThickness: 11
  }]
}))
const duComparisonChartOptions = computed(() => {
  const maxBalance = Math.max(...duComparisonUnits.value.map(unit => Math.abs(unit.balance)), 1)

  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: context => {
            const unit = duComparisonUnits.value[context.dataIndex]
            return [
              ` DU acima em ${unit.above} req. - abaixo em ${unit.below}`,
              ` Saldo: ${unit.balance > 0 ? '+' : ''}${unit.balance} requisitos`
            ]
          }
        }
      }
    },
    scales: {
      x: {
        min: -(maxBalance + 1),
        max: maxBalance + 1,
        ticks: {
          stepSize: 1,
          callback: value => Number.isInteger(value) ? value : '',
          font: { size: 10 }
        },
        grid: { color: 'rgba(0,0,0,.05)' }
      },
      y: {
        ticks: { font: { size: 10 } },
        grid: { display: false }
      }
    }
  }
})
const themeGroups = computed(() => [
  { label: 'AP', name: 'Alta Performance', color: '#0d3b8c', units: filteredDashboardUnits.value.filter(unit => parseNumber(unit.Nota) >= 80) },
  { label: 'SU', name: 'Sustentavel', color: '#1a6b3a', units: filteredDashboardUnits.value.filter(unit => parseNumber(unit.Nota) >= 50 && parseNumber(unit.Nota) < 80) },
  { label: 'BA', name: 'Basico', color: '#bf9000', units: filteredDashboardUnits.value.filter(unit => parseNumber(unit.Nota) < 50) }
])
const themePerformance = computed(() => buildThemePerformance(requirements.value, dashboardData.value?.levels || []))
const themeChartData = computed(() => ({
  labels: themePerformance.value.themes.map(theme => theme.split(' ')),
  datasets: themeGroups.value.map(group => ({
    label: group.label,
    data: themePerformance.value.themes.map(theme => averageThemeScore(group.units, themePerformance.value.scoresByUnit, theme)),
    backgroundColor: `${group.color}cc`,
    borderRadius: 3
  }))
}))
const themeChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      ticks: { font: { size: 9 }, maxRotation: 0, minRotation: 0 },
      grid: { display: false }
    },
    y: {
      min: 0,
      max: 100,
      ticks: { callback: value => `${value}%`, font: { size: 9 } },
      grid: { color: 'rgba(0,0,0,.05)' }
    }
  }
}
const zeroLinePlugin = {
  id: 'sgu-zero-line',
  afterDraw (chart) {
    const context = chart.ctx
    const xAxis = chart.scales.x
    const yAxis = chart.scales.y
    const zero = xAxis.getPixelForValue(0)

    context.save()
    context.beginPath()
    context.moveTo(zero, yAxis.top)
    context.lineTo(zero, yAxis.bottom)
    context.strokeStyle = 'rgba(0,0,0,.15)'
    context.lineWidth = 1
    context.setLineDash([4, 4])
    context.stroke()
    context.restore()
  }
}
const lowVariabilityRequirements = computed(() => {
  return requirements.value
    .map(requirement => {
      const levels = [
        { name: 'Alta Performance', value: distributionValue(requirement, 'highPerformance', 'ap') },
        { name: 'Sustentavel', value: distributionValue(requirement, 'sustainable', 'su') },
        { name: 'Basico', value: distributionValue(requirement, 'basic', 'ba') },
        { name: 'Nao Atende', value: distributionValue(requirement, 'notMet', 'na') }
      ]
      const total = levels.reduce((sum, level) => sum + level.value, 0)
      const dominant = levels.sort((a, b) => b.value - a.value)[0]

      return {
        ...requirement,
        dominantLevel: dominant.name,
        dominance: total ? Math.round((dominant.value / total) * 100) : 0
      }
    })
    .filter(requirement => requirement.dominance >= 85)
    .sort((a, b) => b.dominance - a.dominance)
    .slice(0, 10)
})
const consolidatedRequirements = computed(() => {
  return [...requirements.value]
    .sort((a, b) => Number(b.average) - Number(a.average))
    .slice(0, 10)
})
const difficultRequirements = computed(() => {
  return [...requirements.value]
    .sort((a, b) => Number(a.average) - Number(b.average))
    .slice(0, 10)
})

function parseNumber (value) {
  const normalized = String(value ?? '').trim().replace(',', '.')
  const number = Number(normalized)

  return Number.isFinite(number) ? number : 0
}

function scoreColor (score) {
  if (score >= 80) return '#0d3b8c'
  return score >= 50 ? '#1a6b3a' : '#bf9000'
}

function scoreLayer (score) {
  if (score >= 80) return 'Alta Performance'
  return score >= 50 ? 'Sustentável' : 'Básico'
}

function layerColor (layer) {
  return {
    'Alta Performance': '#0c447c',
    Sustentável: '#3b6d11',
    Básico: '#ba7517',
    'Não Atende': '#e24b4a'
  }[layer] || '#888'
}

function layerClass (layer) {
  return {
    'Alta Performance': 'high',
    Sustentável: 'sustainable',
    Básico: 'basic',
    'Não Atende': 'not-met'
  }[layer] || ''
}

function requirementJitter (unit) {
  const hash = String(unit).split('').reduce((total, character) => total + character.charCodeAt(0), 0)
  return ((hash % 21) - 10) / 100
}

function countUnitsByLayer (units, layer) {
  return units.filter(unit => unit.Camada === layer).length
}

function distributionValue (requirement, field, legacyField) {
  return Number(requirement?.[field] ?? requirement?.[legacyField] ?? 0)
}

function selectRequirementArea (area) {
  selectedRequirementArea.value = area
  selectedRequirementId.value = ''
}

function goToRequirement (requirement) {
  selectedRequirementArea.value = requirement.area || 'Todas'
  selectedRequirementId.value = requirement.id
  activeTab.value = 'requisito'
}

function requirementDescription (layer) {
  if (!activeRequirement.value) return ''

  const apiFields = {
    notMet: 'notMetDescription',
    basic: 'basicDescription',
    sustainable: 'sustainableDescription',
    highPerformance: 'highPerformanceDescription'
  }
  const apiDescription = activeRequirement.value[apiFields[layer]]
  return apiDescription || 'Descrição não disponível para esta camada.'
}

function formatRequirementAverage (value) {
  return parseNumber(value).toFixed(1)
}

function requirementAverageColor (value) {
  const average = parseNumber(value)
  if (average >= 2.25) return '#0d3b8c'
  if (average >= 1.5) return '#1a6b3a'
  if (average >= 0.75) return '#bf9000'
  return '#c0001d'
}

function requirementDistribution (requirement) {
  return [
    { key: 'ap', label: 'Alta Performance', value: distributionValue(requirement, 'highPerformance', 'ap'), className: 'high' },
    { key: 'su', label: 'Sustentavel', value: distributionValue(requirement, 'sustainable', 'su'), className: 'sustainable' },
    { key: 'ba', label: 'Basico', value: distributionValue(requirement, 'basic', 'ba'), className: 'basic' },
  ].filter(layer => layer.value > 0)
}

function requirementScore (average) {
  return Math.round((Number(average || 0) / 3) * 100)
}

async function loadDashboard () {
  loading.value = true
  errorMessage.value = ''
  dashboardData.value = null
  selectedRequirementArea.value = 'Todas'
  selectedRequirementId.value = ''
  selectedArea.value = 'Todas'
  selectedRegional.value = 'Todas'
  selectedIndicator.value = ''
  dataStatus.value = 'Carregando ciclo...'

  try {
    const response = await ImportedModulesService.getSguDashboard(cycleId.value || undefined)
    cycles.value = response.data?.cycles || []
    cycleId.value = response.data?.selectedCycleId || ''
    dashboardData.value = response.data?.data || null
    dataStatus.value = dashboardData.value?.updatedAt
      ? 'Atualizado em ' + dashboardData.value.updatedAt
      : 'Ciclo sem avaliações concluídas'
  } catch (error) {
    errorMessage.value = error.response?.data?.message || 'Falha ao consultar o ciclo do SGU.'
    dataStatus.value = 'Erro ao carregar'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.sgu-page {
  width: 100%;
  max-width: 1540px;
  margin: 0 auto;
  padding: 20px 28px;
  color: #1a1a2e;
}

.sgu-header {
  background: #c0001d;
  color: #fff;
  border-radius: 10px;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.sgu-header h1 {
  font-size: 18px;
  margin: 0 0 4px;
}

.sgu-header p {
  font-size: 12px;
  opacity: .9;
}

.sgu-actions {
  display: grid;
  justify-items: end;
  gap: 8px;
}

.sgu-action-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.sgu-semester {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  gap: 8px;
  margin: 0;
  min-height: 44px;
}

.sgu-semester span {
  color: #ffd0d0;
  font-size: 10px;
  text-transform: uppercase;
}

.sgu-semester select {
  min-width: 130px;
  height: 44px;
  box-sizing: border-box;
  border: 1px solid rgba(255, 255, 255, .45);
  border-radius: 6px;
  background: #fff;
  color: #333;
  padding: 0 10px;
}

.sgu-import {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.sgu-file {
  display: flex;
  align-items: center;
  max-width: 210px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, .6);
  border-radius: 6px;
  padding: 0 12px;
  color: #fff;
  cursor: pointer;
}

.sgu-file span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sgu-file input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.sgu-import button {
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: #fff;
  color: #c0001d;
  font-weight: 600;
  padding: 0 14px;
  cursor: pointer;
}

.sgu-import button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.sgu-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, .65);
  border-radius: 6px;
  background: transparent;
  color: #fff;
  padding: 0 12px;
  cursor: pointer;
}

.sgu-delete:hover {
  background: rgba(255, 255, 255, .12);
}

.sgu-delete:disabled {
  opacity: .55;
  cursor: not-allowed;
}

.sgu-wizard-link {
  align-items: center;
  background: #fff;
  border-radius: 6px;
  box-sizing: border-box;
  color: #c0001d;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  height: 44px;
  justify-content: center;
  padding: 0 14px;
  text-decoration: none;
}

.sgu-wizard-link:hover {
  color: #9d0018;
  text-decoration: none;
}
.sgu-wizard-link.active { background: #09115b; color: #fff; }

.sgu-status {
  align-items: center;
  color: #ffd0d0;
  display: flex;
  font-size: 10px;
  gap: 12px;
  text-align: right;
}
.sgu-status a { color: #fff; text-decoration: underline; }

.sgu-shell {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-top: 0;
  border-radius: 0 0 10px 10px;
  padding: 12px;
  margin-bottom: 16px;
}

.sgu-tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e8eaf0;
  margin-bottom: 14px;
}

.sgu-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 4px 4px;
}

.sgu-filters button {
  min-height: 30px;
  border: 1px solid #e0e3ea;
  border-radius: 16px;
  background: #fff;
  color: #666;
  padding: 5px 13px;
  font-size: 10px;
  cursor: pointer;
}

.sgu-filters button:hover {
  border-color: #c0001d;
  color: #c0001d;
}

.sgu-filters button.active {
  border-color: #c0001d;
  background: #c0001d;
  color: #fff;
}

.sgu-requirement-filters {
  display: grid;
  grid-template-columns: minmax(230px, 1fr) minmax(0, 3fr);
  gap: 16px;
}

.sgu-requirement-filters > div {
  min-width: 0;
}

.sgu-requirement-filters > div > span {
  display: block;
  margin: 0 4px 7px;
  color: #888;
  font-size: 9px;
  text-transform: uppercase;
}

.sgu-requirement-filters .sgu-filters {
  max-height: 106px;
  align-content: flex-start;
  overflow-y: auto;
}

.sgu-requirement-filters .sgu-filters button {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sgu-tab {
  border: 0;
  background: transparent;
  padding: 12px 18px;
  color: #777;
  cursor: pointer;
  border-bottom: 3px solid transparent;
}

.sgu-tab.active {
  color: #c0001d;
  border-bottom-color: #c0001d;
  font-weight: 700;
}

.sgu-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
}

.sgu-overview > .sgu-grid {
  margin-bottom: 12px;
}

.sgu-overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.sgu-overview-card {
  min-width: 0;
}

.sgu-overview-card > h2 {
  margin: 0 0 4px;
  font-size: 14px;
  text-transform: uppercase;
}

.sgu-overview-card > p {
  min-height: 30px;
  margin: 0 0 14px;
  color: #888;
  font-size: 10px;
  line-height: 1.5;
}

.sgu-bars,
.sgu-comparison-list,
.sgu-requirement-list,
.sgu-ranked-list {
  max-height: 330px;
  overflow-y: auto;
  padding-right: 4px;
}

.sgu-bar-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 38px;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  font-size: 10px;
}

.sgu-bar-row > strong {
  color: #555;
  text-align: right;
}

.sgu-bar-row > span {
  color: #777;
  text-align: right;
}

.sgu-bar-track,
.sgu-theme-track {
  height: 10px;
  border-radius: 3px;
  background: #eef0f4;
  overflow: hidden;
}

.sgu-bar-track span,
.sgu-theme-track span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #c0001d;
}

.sgu-comparison-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 52px;
  align-items: center;
  gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #eef0f4;
  font-size: 10px;
}

.sgu-comparison-row > div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #777;
}

.sgu-comparison-row > b {
  color: #777;
  text-align: right;
}

.sgu-comparison-row > b.positive {
  color: #1a6b3a;
}

.sgu-comparison-row > b.negative {
  color: #c0001d;
}

.sgu-theme-list {
  display: grid;
  gap: 13px;
}

.sgu-theme-list > div > div:first-child {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 5px;
  color: #555;
  font-size: 10px;
}

.sgu-theme-track {
  height: 12px;
}

.sgu-theme-track span {
  background: #1a6b3a;
}

.sgu-chart {
  position: relative;
  width: 100%;
}

.sgu-comparison-chart {
  min-height: 400px;
}

.sgu-theme-chart {
  height: 240px;
}

.sgu-chart-legend {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.sgu-chart-legend span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #555;
  font-size: 10px;
}

.sgu-chart-legend i {
  width: 9px;
  height: 9px;
  border-radius: 2px;
}

.sgu-requirement-list > div {
  padding: 8px 0;
  border-bottom: 1px solid #eef0f4;
}

.sgu-requirement-list > div > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #444;
  font-size: 10px;
}

.sgu-requirement-list small {
  display: block;
  margin-top: 3px;
  color: #888;
}

.sgu-inline-empty {
  border-radius: 6px;
  background: #f8f9fb;
  color: #888;
  padding: 20px;
  font-size: 11px;
  text-align: center;
}

.sgu-overview-card.consolidated {
  border-top: 3px solid #1a6b3a;
}

.sgu-overview-card.difficulty {
  border-top: 3px solid #c0001d;
}

.sgu-overview-card.consolidated > h2,
.sgu-overview-card.difficulty > h2 {
  font-size: 10px;
  letter-spacing: .04em;
}

.sgu-ranked-list {
  max-height: none;
}

.sgu-ranked-header {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 36px;
  align-items: center;
  gap: 8px;
  padding: 4px 0 6px;
  border-bottom: 1px solid #eef0f4;
  color: #aaa;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
}

.sgu-ranked-header span:last-child {
  text-align: right;
}

.sgu-ranked-list > button {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) 36px;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 0;
  border: 0;
  border-bottom: 1px solid #eef0f4;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.sgu-ranked-list > button:hover {
  border-radius: 6px;
  background: #f8f9ff;
}

.sgu-ranked-list > button > span {
  color: #aaa;
  font-size: 10px;
  text-align: right;
}

.sgu-ranked-list > button > div {
  min-width: 0;
}

.sgu-ranked-list strong,
.sgu-ranked-list small {
  display: block;
}

.sgu-ranked-list strong {
  color: #444;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
}

.sgu-ranked-list small {
  margin-top: 2px;
  color: #999;
  font-size: 9px;
}

.sgu-ranked-list b {
  font-size: 11px;
  text-align: right;
}

.sgu-ranked-distribution {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 3px;
}

.sgu-ranked-distribution em {
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 9px;
  font-style: normal;
  font-weight: 600;
}

.sgu-ranked-distribution .high { background: #e3eaf8; color: #0c447c; }
.sgu-ranked-distribution .sustainable { background: #e3f5ec; color: #3b6d11; }
.sgu-ranked-distribution .basic { background: #fdf3d8; color: #ba7517; }
.sgu-ranked-distribution .not-met { background: #fde8e8; color: #e24b4a; }
.sgu-ranked-distribution .not-applicable { background: #f0f0f0; color: #888; }

.sgu-overview-empty {
  margin-top: 12px;
}

.sgu-kpi,
.sgu-card {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 10px;
  padding: 16px;
}

.sgu-grid .sgu-kpi {
  border-top: 3px solid transparent;
}

.sgu-kpi span {
  display: block;
  color: #777;
  font-size: 10px;
  text-transform: uppercase;
}

.sgu-kpi strong {
  display: block;
  font-size: 26px;
  margin: 6px 0;
}

.sgu-kpi small {
  color: #999;
}

.sgu-content {
  width: 100%;
}

.sgu-area-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.sgu-area-detail-layout.selected {
  display: grid;
  grid-template-columns: minmax(300px, 1fr) minmax(460px, 1.35fr);
  gap: 12px;
  align-items: start;
}

.sgu-area-detail-layout.selected .sgu-area-grid {
  grid-template-columns: 1fr;
}

.sgu-area-requirements {
  min-width: 0;
  padding: 0;
  overflow: hidden;
}

.sgu-area-requirements > .sgu-card-heading {
  padding: 16px 18px;
  border-bottom: 1px solid #eef0f4;
}

.sgu-area-requirements .sgu-table-wrap {
  max-height: 430px;
  overflow: auto;
}

.sgu-area-card {
  min-width: 0;
}

.sgu-card-heading,
.sgu-requirements-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.sgu-card-heading h2,
.sgu-requirements-toolbar h2 {
  margin: 0 0 4px;
  font-size: 16px;
}

.sgu-card-heading p,
.sgu-requirements-toolbar p {
  margin: 0;
  color: #888;
  font-size: 11px;
}

.sgu-card-heading > strong {
  color: #c0001d;
  font-size: 28px;
}

.sgu-distribution {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #eef0f4;
}

.sgu-distribution div {
  min-width: 0;
}

.sgu-distribution span {
  display: block;
  min-height: 28px;
  color: #888;
  font-size: 9px;
  text-transform: uppercase;
}

.sgu-distribution strong {
  display: block;
  font-size: 18px;
}

.sgu-requirements {
  padding: 0;
  overflow: hidden;
}

.sgu-requirements-toolbar {
  padding: 16px 18px;
  border-bottom: 1px solid #e8eaf0;
}

.sgu-requirements-toolbar label {
  display: grid;
  gap: 4px;
  color: #777;
  font-size: 10px;
  text-transform: uppercase;
}

.sgu-requirements-toolbar select {
  min-width: 220px;
  height: 36px;
  border: 1px solid #d9dde7;
  border-radius: 6px;
  background: #fff;
  padding: 0 10px;
}

.sgu-table-wrap {
  overflow-x: auto;
}

.sgu-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.sgu-table th,
.sgu-table td {
  padding: 11px 12px;
  border-bottom: 1px solid #eef0f4;
  text-align: left;
  vertical-align: middle;
}

.sgu-table th {
  background: #f8f9fb;
  color: #777;
  font-size: 9px;
  text-transform: uppercase;
  white-space: nowrap;
}

.sgu-table tbody tr:hover {
  background: #fafbfc;
}

.sgu-score {
  display: inline-block;
  min-width: 48px;
  border-radius: 4px;
  background: #fde8eb;
  color: #c0001d;
  font-weight: 700;
  padding: 4px 7px;
  text-align: center;
}

.sgu-empty {
  border: 1px dashed #d9dde7;
  border-radius: 8px;
  background: #fff;
  color: #888;
  padding: 32px;
  text-align: center;
}

.sgu-indicator-detail {
  display: grid;
  gap: 12px;
}

.sgu-indicator-intro {
  color: #888;
  font-size: 11px;
  line-height: 1.5;
}

.sgu-indicator-big-numbers {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.sgu-indicator-big-numbers .sgu-kpi {
  border-top: 3px solid transparent;
}

.sgu-indicator-analysis {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 1fr);
  gap: 12px;
  align-items: start;
}

.sgu-indicator-chart-card,
.sgu-associated-requirements,
.sgu-indicator-results {
  margin-bottom: 0;
}

.sgu-indicator-chart-card > h2,
.sgu-associated-requirements > h2,
.sgu-indicator-results > h2 {
  border-bottom: 2px solid #f0f2f8;
  color: #333;
  font-size: 10px;
  font-weight: 700;
  padding-bottom: 8px;
  text-transform: uppercase;
}

.sgu-indicator-chart-card > p,
.sgu-associated-requirements > p {
  color: #aaa;
  font-size: 10px;
  line-height: 1.5;
  margin: 10px 0;
}

.sgu-indicator-chart {
  height: 260px;
  position: relative;
}

.sgu-indicator-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.sgu-indicator-legend span {
  align-items: center;
  color: #555;
  display: flex;
  font-size: 10px;
  gap: 4px;
}

.sgu-indicator-legend i {
  border-radius: 50%;
  display: inline-block;
  height: 10px;
  width: 10px;
}

.sgu-indicator-explanation {
  color: #555 !important;
  margin-top: 10px !important;
}

.sgu-associated-requirements {
  background: #f8f9fb;
}

.sgu-associated-requirements button {
  align-items: center;
  background: #fff9f9;
  border: 0;
  border-left: 2px solid #fca5a5;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  padding: 8px 10px;
  text-align: left;
  width: 100%;
}

.sgu-associated-requirements button:hover {
  background: #fff1f2;
}

.sgu-associated-requirements button span,
.sgu-associated-requirements button strong,
.sgu-associated-requirements button small {
  display: block;
}

.sgu-associated-requirements button small {
  color: #c0001d;
  font-size: 9px;
}

.sgu-associated-requirements button strong {
  color: #333;
  font-size: 11px;
  font-weight: 500;
  margin-top: 3px;
}

.sgu-associated-requirements button b {
  color: #aaa;
  font-size: 11px;
}

.sgu-indicator-results .sgu-table-wrap {
  margin-top: 10px;
}

.sgu-indicator-results th:nth-child(n+4),
.sgu-indicator-results td:nth-child(n+4) {
  text-align: center;
}

.sgu-layer-badge {
  border-radius: 3px;
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
}

.sgu-layer-badge.high {
  background: #e3eaf8;
  color: #0c447c;
}

.sgu-layer-badge.sustainable {
  background: #e3f5ec;
  color: #3b6d11;
}

.sgu-layer-badge.basic {
  background: #fdf3d8;
  color: #ba7517;
}

.sgu-layer-badge.not-met {
  background: #fde8e8;
  color: #e24b4a;
}

.sgu-layer-difference {
  color: #c0001d;
  font-size: 9px;
  margin-left: 3px;
}

.sgu-result-number {
  color: #081051;
  font-weight: 600;
}

.sgu-requirement-detail {
  display: grid;
  gap: 12px;
}

.sgu-requirement-title {
  display: flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #e0e3ea;
  border-left: 3px solid #c0001d;
  border-radius: 0 7px 7px 0;
  background: #fff;
  padding: 12px 14px;
  font-size: 11px;
}

.sgu-requirement-title span::before {
  content: '-';
  margin-right: 7px;
  color: #aaa;
}

.sgu-layer-descriptions {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.sgu-layer-descriptions > div {
  min-height: 98px;
  border-radius: 7px;
  padding: 11px 12px;
}

.sgu-layer-descriptions strong {
  font-size: 9px;
  text-transform: uppercase;
}

.sgu-layer-descriptions p {
  margin: 7px 0 0;
  color: #555;
  font-size: 10px;
  line-height: 1.5;
}

.sgu-layer-descriptions .not-met {
  background: #fde8e8;
  color: #e24b4a;
}

.sgu-layer-descriptions .basic {
  background: #fdf3d8;
  color: #ba7517;
}

.sgu-layer-descriptions .sustainable {
  background: #e3f5ec;
  color: #3b6d11;
}

.sgu-layer-descriptions .high {
  background: #e3eaf8;
  color: #0c447c;
}

.sgu-requirement-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
}

.sgu-requirement-kpis article {
  min-width: 0;
  border: 1px solid #e0e3ea;
  border-top: 3px solid #aaa;
  border-radius: 7px;
  background: #fff;
  padding: 13px;
}

.sgu-requirement-kpis span {
  display: block;
  color: #888;
  font-size: 9px;
  text-transform: uppercase;
}

.sgu-requirement-kpis strong {
  display: block;
  margin-top: 6px;
  font-size: 24px;
}

.sgu-requirement-kpis .not-met { border-top-color: #e24b4a; color: #e24b4a; }
.sgu-requirement-kpis .basic { border-top-color: #ba7517; color: #ba7517; }
.sgu-requirement-kpis .sustainable { border-top-color: #3b6d11; color: #3b6d11; }
.sgu-requirement-kpis .high { border-top-color: #0c447c; color: #0c447c; }
.sgu-requirement-kpis .not-applicable { border-top-color: #aaa; color: #888; }

.sgu-requirement-results {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.sgu-requirement-results h2 {
  margin: 0 0 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef0f4;
  font-size: 11px;
  text-transform: uppercase;
}

.sgu-requirement-chart-card > p,
.sgu-requirement-unit-results > p {
  margin: 0 0 10px;
  color: #aaa;
  font-size: 10px;
  line-height: 1.5;
}

.sgu-requirement-chart {
  position: relative;
  height: 240px;
}

.sgu-requirement-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.sgu-requirement-legend span {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #555;
  font-size: 10px;
}

.sgu-requirement-legend i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.sgu-requirement-chart-card .sgu-requirement-explanation {
  margin-top: 10px;
  color: #555;
}

.sgu-no-indicator {
  border-left: 3px solid #e0e3ea;
  border-radius: 0 7px 7px 0;
  background: #f8f9fb;
  padding: 12px 14px;
  color: #555;
  font-size: 11px;
  line-height: 1.7;
}

.sgu-no-indicator p {
  margin: 2px 0 0;
}

.sgu-requirement-unit-results .sgu-table-wrap {
  max-height: 430px;
  overflow-y: auto;
}

.sgu-requirement-unit-results .sgu-table th {
  position: sticky;
  top: 0;
  z-index: 1;
}

.sgu-muted-result {
  color: #888;
}

.sgu-error {
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  padding: 12px;
  border-radius: 8px;
}

@media (max-width: 900px) {
  .sgu-page {
    padding: 16px;
  }

  .sgu-header {
    align-items: stretch;
    flex-direction: column;
  }

  .sgu-actions {
    justify-items: start;
  }

  .sgu-action-row {
    justify-content: flex-start;
  }

  .sgu-status {
    text-align: left;
  }

  .sgu-grid {
    grid-template-columns: 1fr 1fr;
  }

  .sgu-area-grid {
    grid-template-columns: 1fr;
  }

  .sgu-area-detail-layout.selected {
    grid-template-columns: 1fr;
  }

  .sgu-overview-grid {
    grid-template-columns: 1fr;
  }

  .sgu-indicator-analysis {
    grid-template-columns: 1fr;
  }

  .sgu-requirement-filters {
    grid-template-columns: 1fr;
  }

  .sgu-layer-descriptions,
  .sgu-requirement-results {
    grid-template-columns: 1fr 1fr;
  }

  .sgu-indicator-big-numbers {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .sgu-action-row {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .sgu-import,
  .sgu-file,
  .sgu-semester,
  .sgu-semester select {
    width: 100%;
    max-width: none;
  }

  .sgu-import {
    align-items: stretch;
    flex-direction: column;
  }

  .sgu-grid {
    grid-template-columns: 1fr;
  }

  .sgu-requirements-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .sgu-requirements-toolbar select {
    width: 100%;
    min-width: 0;
  }

  .sgu-distribution {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sgu-indicator-big-numbers {
    grid-template-columns: 1fr;
  }

  .sgu-layer-descriptions,
  .sgu-requirement-kpis,
  .sgu-requirement-results {
    grid-template-columns: 1fr;
  }
}
</style>
